/**
 * Maps a USGS turbidity reading (parameter 63680, in FNU) to a suggested
 * water-clarity label, snapped to the angler's available options.
 *
 * Turbidity bands (FNU) → clarity:
 *   < 10   Clear
 *   10–25  Slightly Stained
 *   25–50  Stained
 *   > 50   Muddy
 *
 * The options list is angler/tournament-configurable (Firestore
 * waterClarityOptions), so we match by canonical label first, then by
 * keyword, then fall back to a positional mapping across whatever options
 * exist. Returns null when there's no turbidity reading or no options.
 */
const CANON = ["clear", "slightly stained", "stained", "muddy"];
const KEYWORDS = [["clear"], ["slightly"], [], ["mud", "murk", "dirty", "chocolate"]];

function bandForTurbidity(fnu) {
  if (fnu < 10) return 0;
  if (fnu < 25) return 1;
  if (fnu < 50) return 2;
  return 3;
}

export function suggestClarityFromTurbidity(turbidityFNU, options) {
  const t = Number(turbidityFNU);
  if (turbidityFNU == null || Number.isNaN(t)) return null;
  const opts = (Array.isArray(options) ? options : []).filter(Boolean);
  if (opts.length === 0) return null;

  const band = bandForTurbidity(t);
  const norm = (s) => String(s).trim().toLowerCase();

  // 1. Exact canonical-label match (handles the default four options and
  //    avoids the "stained" substring colliding with "slightly stained").
  const exact = opts.find((o) => norm(o) === CANON[band]);
  if (exact) return exact;

  // 2. Keyword match (skips the ambiguous "stained" band, index 2).
  const kw = KEYWORDS[band];
  if (kw.length) {
    const hit = opts.find((o) => kw.some((k) => norm(o).includes(k)));
    if (hit) return hit;
  }

  // 3. Positional fallback — spread the 0–3 band across the options list.
  const idx = Math.min(opts.length - 1, Math.round((band / 3) * (opts.length - 1)));
  return opts[idx];
}

export default suggestClarityFromTurbidity;
